// src/hooks/useSnpInterventions.ts
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchSnpInterventions } from "../../api/geneCodes/SnpInterventionsApi";
import { PanelIntervention, SnpInterventionsResponse } from "../../types/geneCodes/InterventionTypes";

export const SNP_INTERVENTIONS_QUERY_KEY = "snp-interventions";

// Module level so a disabled or failed query keeps the same reference between renders —
// the wheels put `modifierMap` in a useCallback dependency array.
const EMPTY_MAP: Map<string, string[]> = new Map();
const EMPTY_INTERVENTIONS: PanelIntervention[] = [];

export interface UseSnpInterventionsResult {
	modifierMap: Map<string, string[]>;
	interventions: PanelIntervention[];
	isLoading: boolean;
	isError: boolean;
}

const byScoreThenCount = (a: PanelIntervention, b: PanelIntervention): number => (b.total_score ?? 0) - (a.total_score ?? 0) || (b.snp_count ?? 0) - (a.snp_count ?? 0);

const derive = (payload?: SnpInterventionsResponse): { modifierMap: Map<string, string[]>; interventions: PanelIntervention[] } => {
	if (!payload || typeof payload !== "object") {
		return { modifierMap: EMPTY_MAP, interventions: EMPTY_INTERVENTIONS };
	}

	const map = new Map<string, string[]>();
	const entries = payload.data && typeof payload.data === "object" ? payload.data : {};

	// Lowercase every key on receipt: labiq's `staging.rs` is ~80% uppercase, `QS_base.rs` is not.
	Object.keys(entries).forEach((rs) => {
		const codes = entries[rs]?.codes;
		map.set(String(rs).toLowerCase(), Array.isArray(codes) ? codes : []);
	});

	const interventions = Array.isArray(payload.interventions) ? [...payload.interventions].sort(byScoreThenCount) : EMPTY_INTERVENTIONS;

	return {
		modifierMap: map.size ? map : EMPTY_MAP,
		interventions: interventions.length ? interventions : EMPTY_INTERVENTIONS,
	};
};

export const useSnpInterventions = (sampleId?: string, category?: string, sex?: string): UseSnpInterventionsResult => {
	const query = useQuery<SnpInterventionsResponse, Error>({
		// Sex is in the key: labiq drops a different wheel per sex, so one sex's answer must never serve another.
		queryKey: [SNP_INTERVENTIONS_QUERY_KEY, sampleId, category, sex],
		queryFn: () => fetchSnpInterventions(sampleId, category, sex),
		enabled: !!sampleId && !!category,
		staleTime: 5 * 60_000,
	});

	const { modifierMap, interventions } = useMemo(() => derive(query.data), [query.data]);

	return {
		modifierMap,
		interventions,
		isLoading: query.isLoading,
		isError: query.isError,
	};
};
